/**
 * Static-mesh optimization helpers.
 *
 * Our levels load hundreds-to-thousands of meshes from Blender-authored
 * environment GLBs (`scrapyard_env.glb`, `overgrown_conservatory_env.glb`,
 * `clockwork_canopy_env.glb`, `scrapyard_chains_env.glb`, etc.). The vast
 * majority of those meshes are dressed scenery that never moves.
 *
 * Babylon, by default, recomputes the world matrix of every mesh every
 * frame so a parent-driven transform update can propagate down. On a
 * 1500-mesh level that's a significant per-frame CPU cost for meshes
 * that haven't moved since they were loaded.
 *
 * `freezeStaticMeshes` walks an InstantiatedModel (or a bare mesh list)
 * and calls `freezeWorldMatrix()` on each leaf. Any mesh whose name
 * matches an opt-out pattern (animated ambient particles, dynamic
 * puzzle elements) is left alone.
 *
 * Reference:
 * https://doc.babylonjs.com/features/featuresDeepDive/scene/optimize_your_scene
 */
import type { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import type { Material } from "@babylonjs/core/Materials/material";
import type { Scene } from "@babylonjs/core/scene";
import type { ShadowGenerator } from "@babylonjs/core/Lights/Shadows/shadowGenerator";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { getAssetManager, type InstantiatedModel } from "../systems/AssetManager";

export interface FreezeStaticOptions {
  /**
   * Regex patterns that mark a mesh as NOT static — those meshes keep
   * their mutable world matrix. Useful for puzzle dials, gear spin
   * rigs, and any mesh the level's update loop mutates by name.
   */
  excludePatterns?: RegExp[];
  /**
   * Whether to also freeze the materials attached to these meshes.
   * Safe when the material properties never change at runtime. Defaults
   * to true because env materials are almost always static.
   */
  freezeMaterials?: boolean;
}

/**
 * Walk `meshes` and freeze the world matrix of each one that is not
 * matched by an exclude pattern. Returns the count of meshes frozen.
 */
export function freezeStaticMeshes(
  meshes: AbstractMesh[],
  options: FreezeStaticOptions = {},
): number {
  const { excludePatterns = [], freezeMaterials = true } = options;
  const frozenMaterials = new Set<Material>();
  let frozen = 0;

  for (const mesh of meshes) {
    if (!mesh.name) continue;
    if (excludePatterns.some((pat) => pat.test(mesh.name))) continue;
    // Skip the glTF root node — it has no geometry and Babylon
    // sometimes re-parents it during instantiation.
    if (mesh.name === "__root__" || mesh.name.startsWith("__root__")) continue;

    // `freezeWorldMatrix` computes the current world matrix once and
    // locks it. After this call, `.position` / `.rotation` / `.scaling`
    // writes to this mesh are silently ignored until `unfreezeWorldMatrix`.
    mesh.freezeWorldMatrix();
    frozen += 1;

    // Tell Babylon the bounding info won't change either — skips
    // per-frame bounding-box recomputation for culling.
    mesh.doNotSyncBoundingInfo = true;

    if (freezeMaterials && mesh.material && !frozenMaterials.has(mesh.material)) {
      // Guard: `freeze()` makes the material immutable for the rest of
      // the level's life. Any runtime color/emissive tweaks (e.g.
      // AuraSight highlights) will no-op on frozen materials, so the
      // caller must exclude those meshes via `excludePatterns`.
      try {
        mesh.material.freeze();
        frozenMaterials.add(mesh.material);
      } catch {
        // Some material subclasses (NodeMaterial, ShaderMaterial) don't
        // support freeze. Skip silently.
      }
    }
  }

  return frozen;
}

/** Per-clone placement of a scattered hero prop. */
export interface ScatterPlacement {
  x: number;
  y: number;
  z: number;
  /** Y-axis rotation in radians. Optional. */
  yaw?: number;
  /** Uniform scale. Defaults to 1. */
  scale?: number;
}

/**
 * Load a GLB once (via AssetManager's cache) and instantiate N clones at
 * world-space placements. Each clone is shadow-casting, receive-shadows
 * on, and has `freezeStaticMeshes` applied so the per-frame matrix cost
 * stays O(1). Returns an array of dispose callbacks the caller should
 * register with their BaseLevel dispose hook.
 *
 * Designed for Meshy-authored hero props that need to appear dozens of
 * times across a level (redwood trunks, vines, chain drapes, ferns,
 * petrified cakes). Fails soft: if the GLB is missing on disk, logs a
 * warning and returns an empty list so the level still boots.
 */
export async function scatterInstances(
  scene: Scene,
  path: string,
  placements: ScatterPlacement[],
  options: {
    shadowGenerator?: ShadowGenerator;
    excludePatterns?: RegExp[];
    freezeMaterials?: boolean;
    tag?: string;
  } = {},
): Promise<{ instances: InstantiatedModel[]; dispose: () => void; wrappers: TransformNode[] }> {
  const instances: InstantiatedModel[] = [];
  const wrappers: TransformNode[] = [];
  const tagPrefix = options.tag ? `scatter_${options.tag}` : "scatter";

  for (let i = 0; i < placements.length; i++) {
    const p = placements[i];
    try {
      const inst = await getAssetManager().instantiateModel(scene, path);
      // The glTF loader gives each clone a `__root__` that carries the
      // right→left handedness conversion — typically `scaling = (1, 1, -1)`
      // plus `rotationQuaternion = (0, 1, 0, 0)` (a 180° Y flip). Earlier
      // scatter builds overwrote those with `root.rotation = <yaw>` +
      // `root.scaling.setAll(scale)`, which (a) nulls the quaternion and
      // (b) replaces the −1 Z flip with a positive value. The result is
      // that every Meshy-authored hero prop (vines, ferns, lens
      // pedestals, constellation beacons, dome segments, clockwork
      // rigging) ends up mirrored along Z and rotated 180° away from
      // the camera — the prop is "there," but its front face points at
      // the backdrop, which reads as flat geometry in playthrough video
      // review (Gemini 2026-04-18 master-valve run: "primitive
      // blockout, cylindrical props with solid colors").
      //
      // The safe fix is to leave the glTF `__root__` untouched and wrap
      // it in a parent TransformNode that owns the scatter pose. The
      // parent's TRS composes with the glTF conversion rather than
      // clobbering it.
      const wrapper = new TransformNode(`${tagPrefix}_${i}`, scene);
      wrapper.position.copyFrom(new Vector3(p.x, p.y, p.z));
      if (p.yaw !== undefined) {
        wrapper.rotationQuaternion = null;
        wrapper.rotation.set(0, p.yaw, 0);
      }
      const s = p.scale ?? 1;
      wrapper.scaling.setAll(s);

      for (const root of inst.rootNodes) {
        if (root instanceof TransformNode) root.parent = wrapper;
      }
      wrappers.push(wrapper);

      for (const mesh of inst.meshes) {
        mesh.isPickable = false;
        if (options.shadowGenerator && !mesh.name.includes("__root__")) {
          options.shadowGenerator.addShadowCaster(mesh);
          mesh.receiveShadows = true;
        }
      }
      freezeStaticMeshes(inst.meshes, {
        excludePatterns: options.excludePatterns,
        freezeMaterials: options.freezeMaterials,
      });
      instances.push(inst);
    } catch (err) {
      // Use a constant format string and pass path/index/err as
      // separate arguments so the format specifier can't be influenced
      // by the asset path.
      console.warn("[scatterInstances] failed to instantiate %s #%d: %o", path, i, err);
    }
  }

  return {
    instances,
    wrappers,
    dispose: (): void => {
      for (const inst of instances) {
        try { inst.dispose(); } catch { /* ignore */ }
      }
      instances.length = 0;
      for (const w of wrappers) {
        try { w.dispose(); } catch { /* ignore */ }
      }
      wrappers.length = 0;
    },
  };
}

/**
 * Default exclude patterns used for environment GLB freezing.
 * Any puzzle/gear/valve/dial/crane/chain mesh that may rotate or move
 * stays unfrozen.
 */
export const DEFAULT_STATIC_EXCLUDES: RegExp[] = [
  /gear/i,
  /valve/i,
  /dial/i,
  /crane/i,
  /pointer/i,
  /crystal/i,
  /portal/i,
  /orrery[^_]*_torus/i,
  /orrery[^_]*_sphere/i,
  /Leg_[FB][LR]/,
  /_Tail$/,
  /_Ear_/,
  /_Head$/,
  /Whisker/i,
  /fern|bloom/i, // emissive flora flickers in some levels
];
